/**
 * Banner 9 — HomeBanner2-style carousel.
 * A per-slide full-bleed background track (each slide can carry its own
 * desktop + mobile background, falling back to the section-wide
 * `image`/`mobile_image` default) sits behind a fixed-width content track,
 * with a dark right strip / hero panel. Per slide (in `metadata` JSON):
 * pre-title, split headline (`title` + `title2`, with `{ }` markers for
 * highlighted words) + optional animated rotating text, subtitle, programme
 * buttons, offer line, a slide background (desktop + mobile), a right-panel
 * hero image, EMI/cost image and accreditation logos (+ heading). Shared
 * display + CTA settings reuse the existing `banner` columns.
 */

export const BANNER_9_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Carousel slides",
  },

  DEFAULT_ROW: {
    id: "1",
    pre_title: "",
    pre_title_color: "",
    pre_title_bg_color: "",
    title: "Transform your career with",
    title2: "an {industry-ready} degree",
    title_color: "",
    title_highlight_color: "",
    animated_text: "",
    subtitle: "Learn from the best, on your schedule.",
    subtitle_color: "",
    cta_label: "",
    cta_side_text: "",
    offer_text: "",
    programs_json: `[
  {"label": "Programme A", "href": "program-slug-a"},
  {"label": "Programme B", "href": "program-slug-b"}
]`,
    slide_background: "",
    slide_background_mobile: "",
    slide_background_alt: "",
    hero_right_image: "",
    alt_hero: "banner",
    cost_emi_image: "",
    cost_emi_image_mobile: "",
    cost_emi_alt: "",
    accreditation_heading: "",
    accreditation_logo_1: "",
    accreditation_logo_1_alt: "",
    accreditation_logo_2: "",
    accreditation_logo_2_alt: "",
    accreditation_logo_3: "",
    accreditation_logo_3_alt: "",
    accreditation_logo_4: "",
    accreditation_logo_4_alt: "",
    priority: 1,
    status: true,
  },

  BANNER_FIELDS_CONFIG: [
    {
      fieldName: "slide_frequency",
      label: "Slide interval (ms)",
      value: 8000,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "8000",
      required: true,
      defaultValue: 8000,
    },
    {
      fieldName: "hero_section_height",
      label: "Section height (px)",
      value: 564,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "564",
      defaultValue: 564,
    },
    {
      fieldName: "hero_container_max_width",
      label: "Content max width (px)",
      value: 1114,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "1114",
      defaultValue: 1114,
    },
    {
      fieldName: "image",
      label: "Section background (desktop, default — used when a slide has no background)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Full-bleed background image",
      defaultValue: "",
    },
    {
      fieldName: "image_alt",
      label: "Section background alt",
      value: "banner-bg",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "banner-bg",
    },
    {
      fieldName: "mobile_image",
      label: "Section background (mobile, default — used when a slide has no mobile background)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Mobile background image",
      defaultValue: "",
    },
    {
      fieldName: "background_color",
      label: "Right strip / hero panel color",
      value: "rgba(32, 30, 30, 0.8)",
      valueType: "string",
      filedType: "RGBA",
      defaultValue: "rgba(32, 30, 30, 0.8)",
    },
    {
      fieldName: "apply_now_show",
      label: "Show primary CTA",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "apply_now_text",
      label: "Primary CTA label",
      value: "Enroll Now",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enroll Now",
      defaultValue: "Enroll Now",
    },
    {
      fieldName: "apply_now_link",
      label: "Primary CTA URL (used when Is CTA is off)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "/contact or https://…",
      defaultValue: "",
    },
    {
      fieldName: "apply_now_is_cta",
      label: "Is CTA — Primary (lead popup)",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "On = open lead capture popup instead of Primary CTA URL",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "button_color",
      label: "Primary CTA background",
      value: "#E1473F",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#E1473F",
    },
    {
      fieldName: "apply_now_color",
      label: "Primary CTA text color",
      value: "#ffffff",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#ffffff",
    },
    {
      fieldName: "download_brochure_show",
      label: "Show Download Brochure button",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      fieldName: "download_brochure_text",
      label: "Download Brochure label",
      value: "Download Brochure",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Download Brochure",
      defaultValue: "Download Brochure",
    },
    {
      fieldName: "download_brochure_link",
      label:
        "Download Brochure URL (optional — when Is CTA is off + empty, uses lead form + course brochure)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://…/brochure.pdf",
      defaultValue: "",
    },
    {
      fieldName: "download_brochure_is_cta",
      label: "Is CTA — Brochure (lead popup / download flow)",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder:
        "On = open brochure lead flow instead of direct brochure URL",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "show_lead_form",
      label: "Show inline lead form (right panel, replaces hero image)",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder:
        "On = right panel renders the inline lead form instead of the slide hero image",
      defaultValue: false,
    },
    {
      fieldName: "enable_lead_form",
      label:
        "Enable lead drop form (form 10 replaces hero image — fixed on desktop, below the banner on mobile)",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder:
        "On = lead form 10 replaces the hero image; it does not slide with the carousel on desktop and renders below the carousel on mobile",
      defaultValue: false,
    },
    {
      fieldName: "carousel_color",
      label: "Carousel dot (active)",
      value: "#E1473F",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#E1473F",
    },
    {
      fieldName: "status",
      label: "Banner status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  METADATA_FIELDS: [
    {
      header: "Pre-title (optional)",
      fieldName: "pre_title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Admissions Open 2026",
      defaultValue: "",
    },
    {
      header: "Pre-title text color (optional — default white/#2A2A2A)",
      fieldName: "pre_title_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Leave empty for default (white on mobile, #2A2A2A on desktop)",
      defaultValue: "",
    },
    {
      header: "Pre-title background color (optional — empty = default pill)",
      fieldName: "pre_title_bg_color",
      value: "",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "Leave empty for default (brown tint mobile / #FFEFEE desktop)",
      defaultValue: "",
    },
    {
      header: "Title line 1 (wrap highlighted words in { })",
      fieldName: "title",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      rows: 2,
      description: "Press Enter to break the text onto the next line.",
      placeholder: "Transform your career with",
      defaultValue: "",
    },
    {
      header: "Title line 2 (wrap highlighted words in { })",
      fieldName: "title2",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      rows: 2,
      description: "Press Enter to break the text onto the next line.",
      placeholder: "an {industry-ready} degree",
      defaultValue: "",
    },
    {
      header: "Title color (both lines — leave empty for default white/black)",
      fieldName: "title_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Leave empty for default (white on mobile, black on desktop)",
      defaultValue: "",
    },
    {
      header: "Title highlight color ({ } words + animated text)",
      fieldName: "title_highlight_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Leave empty for default (yellow / primary)",
      defaultValue: "",
    },
    {
      header:
        "Animated rotating text (comma-separated, optional — appears after Title line 1)",
      fieldName: "animated_text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Leaders, Innovators, Builders",
      defaultValue: "",
    },
    {
      header: "Subtitle",
      fieldName: "subtitle",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Subtitle color (optional — default white on mobile / #5c5c5c on desktop)",
      fieldName: "subtitle_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Leave empty for default (#5c5c5c)",
      defaultValue: "",
    },
    {
      header: "Primary CTA label (this slide — leave empty to hide the CTA)",
      fieldName: "cta_label",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Enroll Now (empty = no CTA button)",
      defaultValue: "",
    },
    {
      header: "CTA side text (right of the CTA, uses CTA background color)",
      fieldName: "cta_side_text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Admissions Open",
      defaultValue: "",
    },
    {
      header: "Offer line (italic, optional)",
      fieldName: "offer_text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Up to 25% scholarship",
      defaultValue: "",
    },
    {
      header:
        "Slide background (desktop) — overrides the section background for this slide",
      fieldName: "slide_background",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Full-bleed background for this slide (desktop)",
      defaultValue: "",
    },
    {
      header:
        "Slide background (mobile, optional — falls back to slide/section background)",
      fieldName: "slide_background_mobile",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Full-bleed background for this slide (mobile)",
      defaultValue: "",
    },
    {
      header: "Slide background alt",
      fieldName: "slide_background_alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Hero image (right panel)",
      fieldName: "hero_right_image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Right-panel hero image (432×432)",
      defaultValue: "",
    },
    {
      header: "Hero image alt",
      fieldName: "alt_hero",
      value: "banner",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "banner",
    },
    {
      header: "EMI / cost image (desktop, optional)",
      fieldName: "cost_emi_image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Cost / EMI strip image (desktop)",
      defaultValue: "",
    },
    {
      header: "EMI / cost image (mobile, optional)",
      fieldName: "cost_emi_image_mobile",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Cost / EMI strip image (mobile)",
      defaultValue: "",
    },
    {
      header: "EMI / cost image alt",
      fieldName: "cost_emi_alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Programme buttons (JSON)",
      fieldName: "programs_json",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: `[{"label":"Programme A","href":"/programme-a"},{"label":"Programme B","href":"/programme-b"}]`,
      defaultValue: "",
      rows: 5,
    },
    {
      header: "Accreditation heading (optional)",
      fieldName: "accreditation_heading",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Accredited by",
      defaultValue: "",
    },
    {
      header: "Accreditation logo 1",
      fieldName: "accreditation_logo_1",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload logo",
      defaultValue: "",
    },
    {
      header: "Accreditation logo 1 alt",
      fieldName: "accreditation_logo_1_alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Accreditation logo 2",
      fieldName: "accreditation_logo_2",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload logo",
      defaultValue: "",
    },
    {
      header: "Accreditation logo 2 alt",
      fieldName: "accreditation_logo_2_alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Accreditation logo 3",
      fieldName: "accreditation_logo_3",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload logo",
      defaultValue: "",
    },
    {
      header: "Accreditation logo 3 alt",
      fieldName: "accreditation_logo_3_alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Accreditation logo 4",
      fieldName: "accreditation_logo_4",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload logo",
      defaultValue: "",
    },
    {
      header: "Accreditation logo 4 alt",
      fieldName: "accreditation_logo_4_alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],
};
